import * as React from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import { cn } from "@finster/ui";
import { SkillMentionNode } from "../skills/components/SkillMentionNode";
import {
  editorSelectionToStringOffsets,
  promptToEditorContent,
  serializeQueryBarDoc,
  skillRangesMatch,
  stringOffsetToPmPos,
  type QueryBarSkillRange,
} from "./queryBarEditorDoc";
import { createQueryBarPlainTextExtensions } from "./queryBarPlainTextExtensions";

type ComposerSelection = {
  start: number;
  end: number;
  direction: "forward" | "backward" | "none";
};

type ComposerAriaProps = {
  role?: string;
  "aria-autocomplete"?: string;
  "aria-controls"?: string;
  "aria-expanded"?: boolean | "true" | "false";
  "aria-activedescendant"?: string;
  "aria-haspopup"?: string | boolean;
};

export const QUERY_BAR_PLACEHOLDER = "Write your prompt here";

export const queryBarEditorContentClassName = cn(
  "min-w-0 bg-transparent",
  "[&_.ProseMirror]:text-sm [&_.ProseMirror]:font-medium [&_.ProseMirror]:text-foreground",
  "[&_.ProseMirror]:whitespace-pre-wrap [&_.ProseMirror]:break-words",
  "[&_.ProseMirror]:outline-none",
  "[&_.ProseMirror_p]:m-0 [&_.ProseMirror_p]:leading-5",
  "[&_.ProseMirror_.node-skillMention]:inline-block [&_.ProseMirror_.node-skillMention]:align-middle",
  "[&_.ProseMirror_.ProseMirror-selectednode]:bg-transparent [&_.ProseMirror_.ProseMirror-selectednode]:outline-none",
  "[&_.ProseMirror_.is-editor-empty:first-child::before]:pointer-events-none",
  "[&_.ProseMirror_.is-editor-empty:first-child::before]:float-left",
  "[&_.ProseMirror_.is-editor-empty:first-child::before]:h-0",
  "[&_.ProseMirror_.is-editor-empty:first-child::before]:font-medium",
  "[&_.ProseMirror_.is-editor-empty:first-child::before]:text-muted-foreground",
  "[&_.ProseMirror_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]"
);

export interface QueryBarPromptEditorProps {
  value: string;
  highlightRanges?: readonly QueryBarSkillRange[];
  disabled?: boolean;
  maxHeightClassName: string;
  inputAriaProps?: ComposerAriaProps;
  onValueChange: (value: string) => void;
  onSelectionChange: (selection: ComposerSelection) => void;
  onKeyDown: (event: KeyboardEvent, editor: Editor) => boolean;
  onFocus: () => void;
  onBlur: () => void;
  editorRef: React.MutableRefObject<Editor | null>;
  inputElementRef: React.MutableRefObject<HTMLElement | null>;
}

export function QueryBarPromptEditor({
  value,
  highlightRanges = [],
  disabled = false,
  maxHeightClassName,
  inputAriaProps,
  onValueChange,
  onSelectionChange,
  onKeyDown,
  onFocus,
  onBlur,
  editorRef,
  inputElementRef,
}: QueryBarPromptEditorProps) {
  const isProgrammaticUpdateRef = React.useRef(false);
  const latestRef = React.useRef({
    value,
    highlightRanges,
    onValueChange,
    onSelectionChange,
    onKeyDown,
    onFocus,
    onBlur,
  });
  latestRef.current = {
    value,
    highlightRanges,
    onValueChange,
    onSelectionChange,
    onKeyDown,
    onFocus,
    onBlur,
  };

  const reportSelection = (currentEditor: Editor) => {
    const offsets = editorSelectionToStringOffsets(currentEditor);
    latestRef.current.onSelectionChange({
      start: offsets.start,
      end: offsets.end,
      direction: "none",
    });
  };

  const editor = useEditor({
    extensions: [...createQueryBarPlainTextExtensions(QUERY_BAR_PLACEHOLDER), SkillMentionNode],
    content: promptToEditorContent(value, highlightRanges),
    autofocus: false,
    editable: !disabled,
    immediatelyRender: true,
    onUpdate: ({ editor: currentEditor, transaction }) => {
      if (!transaction.docChanged || isProgrammaticUpdateRef.current) return;
      const nextValue = serializeQueryBarDoc(currentEditor.getJSON());
      if (nextValue !== latestRef.current.value) {
        latestRef.current.onValueChange(nextValue);
      }
      reportSelection(currentEditor);
    },
    onSelectionUpdate: ({ editor: currentEditor }) => {
      reportSelection(currentEditor);
    },
    onFocus: () => latestRef.current.onFocus(),
    onBlur: () => latestRef.current.onBlur(),
    editorProps: {
      attributes: {
        class: cn(
          maxHeightClassName,
          "min-h-5 overflow-y-auto pb-2 pt-0",
          "text-sm font-medium leading-5",
          "focus:outline-none"
        ),
        placeholder: QUERY_BAR_PLACEHOLDER,
        "aria-label": QUERY_BAR_PLACEHOLDER,
        role: "textbox",
        "aria-multiline": "true",
        ...stringAriaAttributes(inputAriaProps),
      },
      handleKeyDown: (_view, event) => {
        const currentEditor = editorRef.current;
        if (!currentEditor) return false;
        return latestRef.current.onKeyDown(event, currentEditor);
      },
    },
  });

  editorRef.current = editor;

  React.useEffect(() => {
    if (!editor) return undefined;
    const dom = editor.view.dom;
    inputElementRef.current = dom;
    const detachCompat = attachTextareaCompat(dom, editor, latestRef, isProgrammaticUpdateRef);
    return () => {
      detachCompat();
      if (inputElementRef.current === dom) {
        inputElementRef.current = null;
      }
    };
  }, [editor, inputElementRef]);

  React.useEffect(() => {
    editor?.setEditable(!disabled);
    const dom = editor?.view.dom;
    if (!dom) return;
    if (disabled) {
      dom.setAttribute("aria-disabled", "true");
      dom.setAttribute("disabled", "true");
    } else {
      dom.removeAttribute("aria-disabled");
      dom.removeAttribute("disabled");
    }
  }, [disabled, editor]);

  React.useEffect(() => {
    const dom = editor?.view.dom;
    if (!dom) return;
    syncComboboxAria(dom, inputAriaProps);
  }, [editor, inputAriaProps]);

  React.useLayoutEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    const serialized = serializeQueryBarDoc(editor.getJSON());
    const mentionsMatch = skillRangesMatch(editor.getJSON(), highlightRanges);
    if (serialized === value && mentionsMatch) return undefined;

    isProgrammaticUpdateRef.current = true;
    const offsets = editorSelectionToStringOffsets(editor);
    // Keep the serialized string in this frame so `value` reads stay accurate.
    // Mention chips are React node views (portals), so hydrate those after.
    if (serialized !== value) {
      editor.commands.setContent(promptToEditorContent(value), { emitUpdate: false });
    }
    const restoreSelection = () => {
      editor.commands.setTextSelection({
        from: stringOffsetToPmPos(editor, offsets.start),
        to: stringOffsetToPmPos(editor, offsets.end),
      });
    };

    if (highlightRanges.length === 0) {
      editor.commands.setContent(promptToEditorContent(value), { emitUpdate: false });
      restoreSelection();
      isProgrammaticUpdateRef.current = false;
      return undefined;
    }

    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled || editor.isDestroyed) return;
      isProgrammaticUpdateRef.current = true;
      editor.commands.setContent(promptToEditorContent(value, highlightRanges), {
        emitUpdate: false,
      });
      restoreSelection();
      isProgrammaticUpdateRef.current = false;
    });
    return () => {
      cancelled = true;
    };
  }, [editor, highlightRanges, value]);

  if (!editor) return null;

  return <EditorContent editor={editor} className={queryBarEditorContentClassName} />;
}

export function applyQueryBarSelection(
  editor: Editor,
  start: number,
  end: number
): ComposerSelection {
  editor.commands.setTextSelection({
    from: stringOffsetToPmPos(editor, start),
    to: stringOffsetToPmPos(editor, end),
  });
  return { start, end, direction: "none" };
}

function stringAriaAttributes(aria: ComposerAriaProps | undefined): Record<string, string> {
  const attrs: Record<string, string> = {};
  if (!aria) return attrs;
  if (aria.role) attrs.role = aria.role;
  if (aria["aria-autocomplete"]) attrs["aria-autocomplete"] = aria["aria-autocomplete"];
  if (aria["aria-controls"]) attrs["aria-controls"] = aria["aria-controls"];
  if (aria["aria-expanded"] != null) attrs["aria-expanded"] = String(aria["aria-expanded"]);
  if (aria["aria-activedescendant"]) {
    attrs["aria-activedescendant"] = aria["aria-activedescendant"];
  }
  if (aria["aria-haspopup"] != null) attrs["aria-haspopup"] = String(aria["aria-haspopup"]);
  return attrs;
}

function syncComboboxAria(dom: HTMLElement, aria: ComposerAriaProps | undefined): void {
  const next: Record<string, string | undefined> = {
    role: aria?.role ?? "textbox",
    "aria-autocomplete": aria?.["aria-autocomplete"],
    "aria-controls": aria?.["aria-controls"],
    "aria-expanded": aria?.["aria-expanded"] != null ? String(aria["aria-expanded"]) : undefined,
    "aria-activedescendant": aria?.["aria-activedescendant"],
    "aria-haspopup": aria?.["aria-haspopup"] != null ? String(aria["aria-haspopup"]) : undefined,
  };
  for (const [name, value] of Object.entries(next)) {
    if (value == null || value === "") dom.removeAttribute(name);
    else dom.setAttribute(name, value);
  }
}

/**
 * Existing tests (and slash helpers) still treat the composer like a textarea:
 * `value`, `change`, `selectionStart`/`End`, and `setSelectionRange`.
 */
function attachTextareaCompat(
  dom: HTMLElement,
  editor: Editor,
  latestRef: React.MutableRefObject<{
    onValueChange: (value: string) => void;
    onSelectionChange: (selection: ComposerSelection) => void;
  }>,
  isProgrammaticUpdateRef: React.MutableRefObject<boolean>
): () => void {
  const readValue = () => serializeQueryBarDoc(editor.getJSON());
  const readSelection = () => editorSelectionToStringOffsets(editor);
  const writeValue = (text: string) => {
    isProgrammaticUpdateRef.current = true;
    editor.commands.setContent(promptToEditorContent(text), { emitUpdate: false });
    isProgrammaticUpdateRef.current = false;
  };
  const writeSelection = (start: number, end: number) => {
    applyQueryBarSelection(editor, start, end);
    latestRef.current.onSelectionChange({ start, end, direction: "none" });
  };

  Object.defineProperty(dom, "value", {
    configurable: true,
    get: readValue,
    set: writeValue,
  });
  Object.defineProperty(dom, "selectionStart", {
    configurable: true,
    get: () => readSelection().start,
    set: (start: number) => writeSelection(Number(start), readSelection().end),
  });
  Object.defineProperty(dom, "selectionEnd", {
    configurable: true,
    get: () => readSelection().end,
    set: (end: number) => writeSelection(readSelection().start, Number(end)),
  });
  (dom as HTMLTextAreaElement).setSelectionRange = (start, end = start) => {
    writeSelection(start, end);
  };

  const onChange = () => latestRef.current.onValueChange(readValue());
  const onSelect = () => {
    const offsets = readSelection();
    latestRef.current.onSelectionChange({
      start: offsets.start,
      end: offsets.end,
      direction: "none",
    });
  };
  dom.addEventListener("change", onChange);
  dom.addEventListener("select", onSelect);

  return () => {
    dom.removeEventListener("change", onChange);
    dom.removeEventListener("select", onSelect);
  };
}
